import { DONE_STATUSES, isAiAuthored } from './constants';
import { ESCALATION_STATUSES, isEscalationOpen } from './format';
import React, { useMemo, useState } from 'react';

import { Box } from '@mui/material';
import ChatThread from './components/ChatThread';
import ChatsPopover from './components/ChatsPopover';
import ConsentDialog from './components/ConsentDialog';
import DeleteChatDialog from './components/DeleteChatDialog';
import EscalateDialog from './components/EscalateDialog';
import Locale from '/b2b/common/components/Locale';
import { PANEL_MODES } from './state/reducer';
import PanelHeader from './components/PanelHeader';
import PropTypes from 'prop-types';
import TopBar from './components/TopBar';
import classNames from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import styles from './AIAssistant.styles';
import useAIAssistant from './useAIAssistant';

const useStyles = makeStyles(styles);

/*
 * Self-contained, router-agnostic OsanoAI assistant: composes the chat thread with
 * its UI shell (header, conversation-list popover, escalate/delete/consent dialogs)
 * and coordinates the state shared between them. Conversation data and navigation
 * live in the provider, so this never touches the router and works in any panel
 * mode; the fullscreen route wrapper owns deep-link ingest.
 */
const AIAssistant = props => {
    const {
        panelMode = PANEL_MODES.FULLSCREEN,
        className,
        // Defaults to aiAssistant.inputs.message.placeholder via ChatInput's fallback.
        sendMessagePlaceholder,
        // Navigation stays in the route-aware hosts, keeping this shell router-agnostic.
        onOpenFullScreen,
        onNavigate,
    } = props;
    const classes = useStyles(props);

    const {
        activeConversation,
        activeConversationId,
        activeMessages,
        isStreaming,
        openConversation,
        startConversation,
        deleteConversation,
        escalate,
        setPanelMode,
    } = useAIAssistant();

    const [chatsAnchorEl, setChatsAnchorEl] = useState(null);
    const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    /*
     * The escalations raised from this conversation, oldest-first. The server reports
     * them on every conversation read, the list one included, so they survive a list
     * refresh and need no local cache.
     *
     * Keyed on the serialized value rather than the array: a silent poll hands back an
     * equal array under a new reference, and the message list scrolls to the bottom
     * whenever this identity changes.
     */
    const escalationsRaisedKey = JSON.stringify(activeConversation?.escalationsRaised || []);
    const escalationEvents = useMemo(
        () =>
            JSON.parse(escalationsRaisedKey).map(escalation => ({
                id: escalation.id,
                conversationId: escalation.conversationId,
                createdAt: escalation.createdAt,
                label: 'aiAssistant.banners.escalated.label',
                linkText: 'aiAssistant.banners.escalated.viewTicket',
            })),
        [escalationsRaisedKey]
    );

    /*
     * Escalation is allowed once the AI has a completed reply, excluding the
     * streaming tail so the button doesn't flash on during a first response. AI
     * chats only — a Privacy Team thread is already with the team.
     */
    const hasCompletedAssistantReply = activeMessages.some(
        (m, i) =>
            m.role === 'assistant' &&
            m.content &&
            isAiAuthored(m) &&
            !(isStreaming && i === activeMessages.length - 1)
    );
    const canEscalate =
        Boolean(activeConversationId) &&
        // A conversation raised through an escalation is already theirs, open or closed.
        !activeConversation?.escalation &&
        activeConversation?.title &&
        hasCompletedAssistantReply;

    // A conversation is read-only once it's been marked done (closed/pending/spam).
    const isDone = DONE_STATUSES.includes(activeConversation?.status);

    /*
     * A conversation the Privacy Team has closed is read-only too. The thread was
     * theirs from the moment it was raised, so once they resolve it a follow-up
     * belongs in a fresh chat rather than in a closed one — the same way a done
     * conversation behaves.
     */
    const isEscalationResolved =
        activeConversation?.escalation?.status === ESCALATION_STATUSES.RESOLVED;

    /*
     * Paused, not read-only: the message is still persisted and mirrored onto the ticket,
     * only the AI turn is skipped. Same predicate the provider pauses and polls on, so the
     * notice and the send path can't disagree.
     */
    const isEscalationPaused = isEscalationOpen(activeConversation?.escalation);

    /*
     * An agent's reply is the answer the customer was waiting for, so telling them the team
     * is still reviewing directly underneath it is wrong. The pause holds either way — it
     * lifts on resolution, not on a reply — so the notice comes back on their next message.
     */
    const lastMessage = activeMessages[activeMessages.length - 1];
    const isAwaitingPrivacyTeam = !lastMessage?.isEmployee;

    const isReadOnly = isDone || isEscalationResolved;

    /*
     * chat-api stopped writing the lifecycle notices into the transcript, so this is the
     * only thing telling the customer the AI paused or the chat closed. It renders in the
     * transcript, styled as the removed notices were, rather than as a banner.
     */
    const escalationNoticeKey = isEscalationResolved
        ? 'aiAssistant.banners.escalationClosed.message'
        : isEscalationPaused && isAwaitingPrivacyTeam
          ? 'aiAssistant.banners.escalationPaused.message'
          : null;

    // The done status keeps its banner, but never alongside an escalation notice —
    // one conversation, one statement of where it stands.
    const readOnlyMessageKey =
        isDone && !escalationNoticeKey ? 'aiAssistant.banners.closed.message' : null;

    const handleChatsOpen = event => setChatsAnchorEl(event.currentTarget);
    const handleChatsClose = () => setChatsAnchorEl(null);

    const handleNewConversation = () => {
        handleChatsClose();
        startConversation();
    };

    const handleSelectConversation = id => {
        handleChatsClose();
        openConversation(id);
    };

    const handleDeleteConversation = (event, conversation) => {
        event.stopPropagation();
        setDeleteError(null);
        setConversationToDelete(conversation);
    };

    // Ellipsis "Delete chat" acts on the active conversation (no row event to stop).
    const handleDeleteActiveChat = () => {
        setDeleteError(null);
        setConversationToDelete(activeConversation);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        try {
            /*
             * Removing the active conversation nulls it in the reducer (REMOVE),
             * landing the user on a blank chat.
             */
            await deleteConversation(conversationToDelete.id);
            setConversationToDelete(null);
        } catch {
            setDeleteError('common.error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setConversationToDelete(null);
        setDeleteError(null);
    };

    const handleEscalateConfirm = async values => {
        await escalate(activeConversationId, values);
        setEscalateDialogOpen(false);
    };

    // Collapsing dismisses the panel overlay; the page behind it is untouched.
    const handleClosePanel = () => setPanelMode(PANEL_MODES.COLLAPSED);

    const isPanelMode = panelMode === PANEL_MODES.SIDEBAR || panelMode === PANEL_MODES.WIDGET;

    const renderHeader = () => {
        switch (panelMode) {
            case PANEL_MODES.SIDEBAR:
            case PANEL_MODES.WIDGET:
                return (
                    <PanelHeader
                        title={activeConversation?.title}
                        activeConversationId={activeConversationId}
                        panelMode={panelMode}
                        onOpenChats={handleChatsOpen}
                        onNewChat={handleNewConversation}
                        onSetSidePanel={() => setPanelMode(PANEL_MODES.SIDEBAR)}
                        onSetPopup={() => setPanelMode(PANEL_MODES.WIDGET)}
                        onOpenFullScreen={onOpenFullScreen}
                        onClose={handleClosePanel}
                        canEscalate={Boolean(canEscalate)}
                        onEscalate={() => setEscalateDialogOpen(true)}
                        onDelete={handleDeleteActiveChat}
                    />
                );
            case PANEL_MODES.FULLSCREEN:
            default:
                return (
                    <TopBar
                        activeConversationId={activeConversationId}
                        title={activeConversation?.title}
                        canEscalate={Boolean(canEscalate)}
                        onOpenChats={handleChatsOpen}
                        onNewConversation={handleNewConversation}
                        onEscalate={() => setEscalateDialogOpen(true)}
                        onDelete={handleDeleteActiveChat}
                    />
                );
        }
    };

    return (
        <>
            <Box
                className={classNames(
                    classes.root,
                    isPanelMode ? classes.rootPanel : classes.rootFullscreen,
                    className
                )}
            >
                {renderHeader()}
                <Box className={classes.body}>
                    <Box className={classes.main}>
                        <ChatThread
                            compact={isPanelMode}
                            sendMessagePlaceholder={sendMessagePlaceholder}
                            isReadOnly={isReadOnly}
                            readOnlyMessage={
                                readOnlyMessageKey ? <Locale path={readOnlyMessageKey} /> : null
                            }
                            stateNoticeKey={escalationNoticeKey}
                            escalationEvents={escalationEvents}
                            onOpenConversation={openConversation}
                            onNavigate={onNavigate}
                        />
                    </Box>
                </Box>
            </Box>
            <ChatsPopover
                open={Boolean(chatsAnchorEl)}
                anchorEl={chatsAnchorEl}
                onClose={handleChatsClose}
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                onDeleteConversation={handleDeleteConversation}
                panelMode={panelMode}
            />
            <ConsentDialog />
            <EscalateDialog
                open={escalateDialogOpen}
                onClose={() => setEscalateDialogOpen(false)}
                onConfirm={handleEscalateConfirm}
            />
            <DeleteChatDialog
                open={Boolean(conversationToDelete)}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                isLoading={isDeleting}
                error={deleteError}
            />
        </>
    );
};

AIAssistant.propTypes = {
    panelMode: PropTypes.oneOf(Object.values(PANEL_MODES)),
    className: PropTypes.string,
    sendMessagePlaceholder: PropTypes.string,
    onOpenFullScreen: PropTypes.func,
    onNavigate: PropTypes.func,
};

export default AIAssistant;
